import { useReducer } from 'react'

import type { BulkKind, DuplicateConflict } from '../utils/duplicateDecisions'
import {
  bulkTargets,
  duplicateDecisionsReducer,
  ignoredCount,
  INITIAL_DUPLICATE_DECISIONS,
  isComplete,
} from '../utils/duplicateDecisions'

/**
 * The duplicate review's decisions and its two bulk boxes. A box shows ticked
 * only after the admin ticks it.
 *
 * `dispatch` is handed to the rows as is: React keeps its identity, so a
 * memoized row re-renders only when its own decision changes.
 */
export const useDuplicateDecisions = (
  conflicts: readonly DuplicateConflict[]
) => {
  'use memo'
  const [state, dispatch] = useReducer(
    duplicateDecisionsReducer,
    INITIAL_DUPLICATE_DECISIONS
  )

  // The targets are built on the tick, not on every render.
  const setBulk = (kind: BulkKind, checked: boolean) => {
    dispatch(
      checked
        ? { type: 'applyToAll', kind, targets: bulkTargets(conflicts, kind) }
        : { type: 'undoApplyToAll' }
    )
  }

  return {
    decisions: state.decisions,
    dispatch,
    createAllChecked: state.bulk === 'create',
    firstListedChecked: state.bulk === 'firstListed',
    // Every row the API flags has a candidate; this guards the box anyway.
    canFirstListedAll: conflicts.every(
      ({ player_conflicts }) => player_conflicts.length > 0
    ),
    ignoredCount: ignoredCount(state.decisions),
    isComplete: isComplete(state.decisions, conflicts),
    setCreateAll: (checked: boolean) => setBulk('create', checked),
    setFirstListedAll: (checked: boolean) => setBulk('firstListed', checked),
  }
}
